import { Link } from "react-router-dom";
import { useSeo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PILLARS, DIRECTORS } from "@/data/content";

export default function AboutPage() {
  useSeo({
    title: "About Jaybhaye Realty Pvt. Ltd. | Built on the Apratim Group Legacy",
    description:
      "Jaybhaye Realty Pvt. Ltd. represents the next phase of a real-estate journey that began with Apratim Group / Apratim Builders — 15+ years, 900+ families, 15+ acres across Chhatrapati Sambhaji Nagar, now expanding into Pune.",
    path: "/about",
    keywords: "Jaybhaye Realty, about Jaybhaye Realty, Apratim Group, Apratim Builders Aurangabad",
  });

  return (
    <div data-testid="about-page">
      <section className="py-24 border-b border-[#12281a]/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#b38b47] font-bold block mb-6">Our Story</span>
            <h1 className="font-monument text-4xl sm:text-6xl text-[#12281a] leading-[1.02] uppercase mb-8" data-testid="about-headline">
              Built on Experience. Driven by the Next Generation.
            </h1>
            <p className="text-xl text-[#2c382f] font-light leading-snug max-w-3xl">
              Jaybhaye Realty Pvt. Ltd. is the next phase of a real-estate journey that began with Apratim Group —
              a journey measured not in square feet, but in the trust of 900+ families.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white border-b border-[#12281a]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <img
                src="/assets/jb-logo-full.png"
                alt="Jaybhaye Realty Pvt. Ltd. — established 2022"
                className="w-full max-w-xs border border-[#12281a]/15 bg-[#f7f5f0] p-6"
                loading="lazy"
                data-testid="about-logo"
              />
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-6 text-base text-[#2c382f] leading-relaxed">
            <Reveal>
              <p>
                For over fifteen years, the Jaybhaye family — through Apratim Group and Apratim Builders — planned,
                built and delivered residential communities across Chhatrapati Sambhaji Nagar (Aurangabad). Developments
                such as Apratim Radhai Estate, Apratim Park and Apratim Vastu are today settled neighbourhoods,
                home to hundreds of families.
              </p>
            </Reveal>
            <Reveal delay={100}>
              <p>
                Jaybhaye Realty Pvt. Ltd., incorporated in 2022, carries that foundation forward. The company pairs
                generational experience in land, planning and execution with a contemporary, institutionally-minded
                approach — and is now expanding its development footprint from Aurangabad into Pune's premier
                growth corridors.
              </p>
            </Reveal>
            <Reveal delay={150}>
              <p className="font-body-serif italic text-lg text-[#12281a]">
                Our principle remains unchanged: Growing with Integrity.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="mb-16 max-w-2xl">
              <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                Why Jaybhaye Realty
              </span>
              <h2 className="text-3xl sm:text-4xl font-monument text-[#12281a]">Five Principles of Real Value</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div className="border-t border-[#12281a]/20 pt-6" data-testid={`pillar-${p.title.toLowerCase()}`}>
                  <div className="chapter-num mb-2">{p.numeral}</div>
                  <h3 className="text-lg font-monument text-[#12281a] mb-2 font-semibold">{p.title}</h3>
                  <p className="text-xs text-[#2c382f]/80 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white border-t border-[#12281a]/10" data-testid="directors-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                Corporate Governance
              </span>
              <h2 className="text-3xl sm:text-4xl font-monument text-[#12281a]">Board of Directors</h2>
            </div>
          </Reveal>
          <div className="space-y-16 max-w-5xl mx-auto">
            {DIRECTORS.map((d, i) => (
              <Reveal key={d.initials} delay={i * 100}>
                <div
                  className={`grid grid-cols-1 md:grid-cols-12 gap-10 border border-[#12281a]/20 bg-[#f7f5f0] p-8 sm:p-12 items-start ${
                    i % 2 === 1 ? "" : ""
                  }`}
                  data-testid={`about-director-${d.initials.toLowerCase()}`}
                >
                  <div className={`md:col-span-4 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <img
                      src={d.photo}
                      alt={`${d.name}, ${d.role}`}
                      className="w-full aspect-[4/5] object-cover object-top border border-[#b38b47]/40"
                      loading="lazy"
                      data-testid={`director-photo-${d.initials.toLowerCase()}`}
                    />
                  </div>
                  <div className="md:col-span-8">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                      {d.tagline}
                    </span>
                    <h3 className="text-2xl font-monument text-[#12281a]">{d.name}</h3>
                    <span className="text-xs uppercase tracking-widest text-[#12281a]/60 font-semibold block mt-1 mb-6">
                      {d.role}
                    </span>
                    <div className="space-y-4">
                      {d.fullBio.map((para, j) => (
                        <p key={j} className="text-sm text-[#2c382f]/85 leading-relaxed">
                          {para}
                        </p>
                      ))}
                    </div>
                    <p className="mt-6 font-body-serif italic text-base text-[#12281a] border-l-2 border-[#b38b47] pl-4">
                      {d.closing}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <div className="text-center mt-16">
              <Link
                to="/contact"
                data-testid="about-contact-button"
                className="inline-block px-8 py-3.5 bg-[#12281a] hover:bg-[#1f422b] text-[#f7f5f0] text-xs uppercase tracking-widest font-semibold transition-colors"
              >
                Connect with Leadership
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
